import { Head } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { creatorListingsPath, productCreatorStagePath } from "@travily/routes";
import type ListingsCreatorPhotos from "@travily/types/pages/ListingsCreatorPhotos";
import { PRODUCT_PHOTO_PATHS } from "@/hooks/listings/photo-paths";
import { usePhotoManager } from "@/hooks/listings/use-photo-manager";
import { useCreatorTopBarActions } from "@/lib/wizard/use-creator-top-bar-actions";
import { useWizardForm } from "@/lib/wizard/use-wizard-form";
import { ProductPhotosStepView } from "./show.view";

type PageProps = ListingsCreatorPhotos;

type FormData = {
  photos: string;
};

const ProductPhotosStep = ({ listing_id, stage_index, stage_order, photos, min_photos }: PageProps) => {
  const { fieldError, currentStage, totalStages, onBack, onPrimary, primaryPending } = useWizardForm<FormData>({
    routes: {
      stageUrl: (stage) => productCreatorStagePath(listing_id, stage),
      entryUrl: creatorListingsPath(),
    },
    stageName: "photos",
    stageIndex: stage_index,
    stageOrder: stage_order,
    initialData: { photos: "" },
  });
  const manager = usePhotoManager({ initialPhotos: photos, listingId: listing_id, paths: PRODUCT_PHOTO_PATHS });

  const photosError = fieldError("photos");

  const topBarActions = useCreatorTopBarActions({ saveHref: creatorListingsPath() });

  return (
    <>
      <Head title={t("web.products.creator.steps.photos.show.meta_title")}>
        <meta name="description" content={t("web.products.creator.steps.photos.show.meta_description")} />
      </Head>
      <ProductPhotosStepView
        currentStage={currentStage}
        totalStages={totalStages}
        onBack={onBack}
        topBarActions={topBarActions}
        onPrimary={onPrimary}
        primaryPending={primaryPending}
        photos={manager.photos}
        minPhotos={min_photos}
        photosError={photosError}
        onAddPhotos={manager.openModal}
        onDeletePhoto={manager.requestDelete}
        onReorderPhotos={manager.reorderPhotos}
        isUploadModalOpen={manager.isModalOpen}
        uploadFiles={manager.upload.files}
        isUploading={manager.upload.isUploading}
        allUploadsCompleted={manager.upload.allCompleted}
        onAddFiles={manager.upload.addFiles}
        onConfirmUpload={manager.confirmUpload}
        onUploadModalOpenChange={(open) => {
          if (!open) manager.closeModal();
        }}
        onRemoveUploadFile={manager.upload.removeFile}
        isDeleteModalOpen={manager.deleteTarget !== null}
        onConfirmDelete={manager.confirmDelete}
        onDeleteModalOpenChange={(open) => {
          if (!open) manager.cancelDelete();
        }}
      />
    </>
  );
};

export default ProductPhotosStep;
